import { useState, useEffect, useRef } from "react";
import { Center, Heading, useColorModeValue, Flex, SimpleGrid, FormControl, IconButton, useToast, Spinner, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, useDisclosure, Button } from "@chakra-ui/react";
import { datepickerLight, datepickerDark } from "../../contexts/GlobalVariables";
import { getDocs, limit, orderBy, query, collection, doc, deleteDoc } from "firebase/firestore";
import { db } from "../../utils/init-firebase";
import { useAuth } from "../../contexts/AuthContext";
import TopPerformers_Edit from "./TopPerformers_Edit";
import TopPerformers_IndividualBox from "./TopPerformers_IndividualBox";
import TopPerformers_GoogleReviews from "./TopPerformers_GoogleReviews";
import TopPerformers_CompanyStats from "./TopPerformers_CompanyStats";
import { Select } from "chakra-react-select";
import TopPerformers_ReviewCarousel_V2 from "./TopPerformers_ReviewCarousel_V2";
import { FiTrash2 } from "react-icons/fi";

export default function TopPerformers({ }) {
    const { SuperAdmin } = useAuth()
    const [displayedMonth, setDisplayedMonth] = useState(null)
    const [loading, setLoading] = useState(true)
    const toast = useToast()
    const { isOpen, onOpen, onClose } = useDisclosure()
    const cancelRef = useRef()

    const [allMonths, setAllMonths] = useState(null)
    const [options, setOptions] = useState()

    const [reviewList, setReviewList] = useState(null)

    const datepickerClass = useColorModeValue(datepickerLight, datepickerDark)

    const fetchTopPerformers = async () => {
        setLoading(true)
        const q = query(collection(db, "Top Performers"), orderBy('Month Year Timestamp', 'desc'));
        const querySnapshot = await getDocs(q)
        let data = {}
        let options = []
        let index = 0
        querySnapshot.forEach((doc) => {
            data[`${doc.id}`] = doc.data()
            options.push({ value: doc.id, label: doc.id, index: index++ })
        })

        if (options.length > 0) {
            setDisplayedMonth({ label: options[0].value, value: options[0].value, index: 0 })
        } else {
            setDisplayedMonth(null)
        }

        setOptions(options)
        setAllMonths(data)
        setLoading(false)
    }

    const deleteTopPerformer = async () => {
        if (!displayedMonth) return;

        try {
            await deleteDoc(doc(db, "Top Performers", displayedMonth.value));
            toast({
                title: `${displayedMonth.value} Top Performers deleted successfully`,
                status: 'success',
                duration: 5000,
                isClosable: true,
                position: 'top'
            });
            await fetchTopPerformers();
        } catch (err) {
            toast({
                title: "Error deleting Top Performers",
                description: err.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top'
            });
        }
        onClose()
    }

    const fetchReviewList = async () => {
        const q = query(collection(db, "Google Reviews Version 2"), orderBy('timestamp', 'desc'), limit(1));
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
            // Get the original array of reviews
            const allReviews = doc.data().reviews;

            // Filter out reviews from "Impeccable Lending"
            const filteredReviews = allReviews.filter(review => review.location !== 'Impeccable Lending');
            
            // Set the filtered list to state
            setReviewList(filteredReviews);
        });
    }

    useEffect(() => {
        fetchTopPerformers()
        fetchReviewList()
    }, [])

    const awardsList = [
        { key: 'Duke', displayName: 'The Duke' },
        { key: 'Rocky', displayName: 'The Rocky' },
        { key: 'Harry Potter', displayName: 'The Harry Potter' },
        { key: 'Hustler', displayName: 'The Charlie Hustle' },
        { key: 'Loan Ranger', displayName: 'The Loan Ranger' },
        { key: 'Dolly', displayName: 'The Dolly' },
         { key: '$Bill$ Shoe Leather', displayName: '$Bill$ Shoe Leather' },
         { key: 'Taking Care Of Business', displayName: 'Taking Care Of Business' }
    ];

    const validAwards = !loading && allMonths && displayedMonth ? awardsList.map(award => {
        const awardData = allMonths[displayedMonth.value]?.[award.key];
        const hasValidUser = awardData && awardData.User && awardData.User.value;
        return hasValidUser ? { ...award, ...awardData } : null;
    }).filter(Boolean) : [];

    const renderAwards = () => {
        if (validAwards.length === 8) {
            return (
                <>
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing='20px' mb={8}>
                        {validAwards.slice(0, 4).map(award => (
                            <TopPerformers_IndividualBox
                                key={award.key}
                                awardName={award.displayName}
                                amount={award.Amount}
                                user={award.User}
                            />
                        ))}
                    </SimpleGrid>
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing='20px' mb={8}>
                        {validAwards.slice(4, 8).map(award => (
                            <TopPerformers_IndividualBox
                                key={award.key}
                                awardName={award.displayName}
                                amount={award.Amount}
                                user={award.User}
                            />
                        ))}
                    </SimpleGrid>
                </>
            );
        } else if (validAwards.length === 7) {
            return (
                <>
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing='20px' mb={8}>
                        {validAwards.slice(0, 3).map(award => (
                            <TopPerformers_IndividualBox
                                key={award.key}
                                awardName={award.displayName}
                                amount={award.Amount}
                                user={award.User}
                            />
                        ))}
                    </SimpleGrid>
                    <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing='20px' mb={8}>
                        {validAwards.slice(3, 7).map(award => (
                            <TopPerformers_IndividualBox
                                key={award.key}
                                awardName={award.displayName}
                                amount={award.Amount}
                                user={award.User}
                            />
                        ))}
                    </SimpleGrid>
                </>
            );
        } else {
            return (
                <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing='20px' mb={8}>
                    {validAwards.map(award => (
                        <TopPerformers_IndividualBox
                            key={award.key}
                            awardName={award.displayName}
                            amount={award.Amount}
                            user={award.User}
                        />
                    ))}
                </SimpleGrid>
            );
        }
    };

    return (
        <>
            {loading && <Center><Spinner /></Center>}
            {!loading && allMonths && displayedMonth &&
                <>
                    <Flex mb={5} mt={5} flexDir={{ base: 'column-reverse', md: 'row' }} justify='center'>
                        <Flex mb={5} flexDir={{ base: 'column', md: 'row' }} align='center' justify='center'>
                            <Heading mr={2} mb={2} textAlign={'center'} as='em' size='md'>
                                Top Performers
                            </Heading>
                            <Flex align="center">
                                <Heading mr={2} textAlign={'center'} as='em' size='md'>
                                    <FormControl w={'215px'}>
                                        <Select
                                            size="md"
                                            onChange={setDisplayedMonth}
                                            value={displayedMonth}
                                            defaultValue={displayedMonth}
                                            options={options}
                                        />
                                    </FormControl>
                                </Heading>
                                {SuperAdmin && displayedMonth &&
                                    <IconButton
                                        ml={2}
                                        colorScheme="red"
                                        variant="outline"
                                        size="sm"
                                        icon={<FiTrash2 />}
                                        onClick={onOpen}
                                        aria-label="Delete top performer"
                                    />
                                }
                            </Flex>
                        </Flex>
                        {SuperAdmin &&
                            <TopPerformers_Edit
                                fetchTopPerformers={fetchTopPerformers}
                                initialMonthString={displayedMonth.value} />
                        }
                    </Flex>
                    {displayedMonth && allMonths[displayedMonth.value] &&
                        <>
                            {renderAwards()}
                            {allMonths[displayedMonth.value]['Top Google Reviews'] &&
                                <TopPerformers_GoogleReviews data={allMonths[displayedMonth.value]['Top Google Reviews']} />
                            }
                            {allMonths[displayedMonth.value]['Funded Loans - Company'] && allMonths[displayedMonth.value]['Top Google Reviews'] &&
                                <TopPerformers_CompanyStats
                                    data={allMonths[displayedMonth.value]['Funded Loans - Company']}
                                    reviewData={allMonths[displayedMonth.value]['Top Google Reviews']}
                                />
                            }
                        </>
                    }
                </>
            }

            {!loading && (!allMonths || Object.keys(allMonths).length === 0) && SuperAdmin &&
                <Center>
                    <Heading size='md' mr='2'>No Top Performers Found</Heading>
                    <TopPerformers_Edit fetchTopPerformers={fetchTopPerformers} initialMonthString={'January 2022'} />
                </Center>
            }
            {reviewList &&
                <TopPerformers_ReviewCarousel_V2 reviewList={reviewList} />
            }
            <AlertDialog
                isOpen={isOpen}
                leastDestructiveRef={cancelRef}
                onClose={onClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Entry
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Are you sure you want to delete the Top Performers for {displayedMonth?.value}? This action cannot be undone.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={onClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={deleteTopPerformer} ml={3}>
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    )
}